import { type FormEvent, useState } from "react"
import { api } from "../lib/api"
import { Button, Card, ErrorNote, Field, Select, TextArea } from "./ui"

export type SessionWorkspace = { id: string; name: string; repoUrl: string | null }

export function CreateSessionForm({
  workspaces,
  initialWorkspaceId,
  fixedWorkspaceId,
  onCancel,
  onCreated,
}: {
  workspaces: SessionWorkspace[]
  initialWorkspaceId?: string
  fixedWorkspaceId?: string
  onCancel: () => void
  onCreated: (sessionId: string) => void
}) {
  const [workspaceId, setWorkspaceId] = useState(
    fixedWorkspaceId ?? initialWorkspaceId ?? workspaces[0]?.id ?? "",
  )
  const [idleTimeoutMinutes, setIdleTimeoutMinutes] = useState(30)
  const [env, setEnv] = useState("")
  const [startupScript, setStartupScript] = useState("")
  const [error, setError] = useState<unknown>(null)
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setError(null)
    setSubmitting(true)
    try {
      const created = await api.post<{ session: { id: string } }>("/workspace-sessions", {
        workspaceId,
        idleTimeoutMinutes,
        env,
        startupScript,
      })
      onCreated(created.session.id)
    } catch (cause) {
      setError(cause)
      setSubmitting(false)
    }
  }

  return (
    <Card className="p-4">
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        {!fixedWorkspaceId && (
          <Select label="Workspace" value={workspaceId} onChange={(event) => setWorkspaceId(event.target.value)}>
            {workspaces.map((workspace) => (
              <option key={workspace.id} value={workspace.id}>
                {workspace.name}{workspace.repoUrl ? ` — ${workspace.repoUrl}` : ""}
              </option>
            ))}
          </Select>
        )}

        <details className="rounded-md border border-border bg-surface-muted/40">
          <summary className="cursor-pointer select-none px-3 py-2 text-sm font-medium text-foreground">
            Advanced options
          </summary>
          <div className="flex flex-col gap-4 border-t border-border px-3 py-3">
            <Field
              label="Idle timeout (minutes)"
              type="number"
              value={String(idleTimeoutMinutes)}
              onChange={(event) => setIdleTimeoutMinutes(Number(event.target.value))}
              min={5}
              max={1440}
              required
            />
            <TextArea
              label="Environment variables"
              value={env}
              onChange={(event) => setEnv(event.target.value)}
              rows={5}
              spellCheck={false}
              placeholder={"API_KEY=sk-...\nDATABASE_URL=postgres://localhost/db"}
              className="font-mono"
              hint="One KEY=VALUE per line. Values are encrypted and never returned by the API."
            />
            <TextArea
              label="Startup Bash script"
              value={startupScript}
              onChange={(event) => setStartupScript(event.target.value)}
              rows={7}
              spellCheck={false}
              placeholder={"#!/usr/bin/env bash\nbun install\npython -m venv .venv"}
              className="font-mono"
              hint="Runs once in the background from the cloned repository. Maximum 64 KiB and 15 minutes."
            />
          </div>
        </details>

        <ErrorNote error={error} />
        <div className="flex gap-2">
          <Button type="submit" disabled={submitting || !workspaceId}>
            {submitting ? "Creating..." : "Create Session"}
          </Button>
          <Button variant="ghost" type="button" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  )
}
